import {
  Badge,
  Box,
  Button,
  Collapsible,
  Field,
  Grid,
  HStack,
  NativeSelect,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { toasterApi } from '@ui';
import React from 'react';
import {
  LuChevronDown,
  LuChevronLeft,
  LuExpand,
  LuFileText,
  LuTag,
} from 'react-icons/lu';
import { useNavigate } from 'react-router';
import { TrackingApi } from '@/state/api';
import { useAppSelector } from '@/state';
import { TrackingType } from '@/types';
import { KARAS_RED, newRequestId, openPdfBlob, printPdfBlob } from '../utils';
import { ItemsPanel } from './ItemsPanel';
import { JobPanel } from './JobPanel';
import type { ComposerLaunch } from './LabelComposerProvider';
import { PreviewPanel } from './PreviewPanel';
import { SavedSettingsManager } from './SavedSettingsManager';
import { SettingsPanel } from './SettingsPanel';

export const DRAFT_PREFIX = 'karas.tracking.draft.';

type StepId = 'items' | 'customize' | 'review';

interface Draft {
  selections: TrackingType.ComposerSelection[];
  pendingSources: TrackingType.ComposerSource[];
  labelSpec: TrackingType.LabelSpec | null;
  profileSpec: TrackingType.ProfileSpec | null;
  templateId: number | null;
  profileId: number | null;
  unit: 'mm' | 'in';
  mode: 'labels' | 'reports';
  paper: 'letter' | 'a4';
  step?: StepId;
  context?: ComposerLaunch['context'];
}

const STEPS: { id: StepId; label: string }[] = [
  { id: 'items', label: 'Choose items' },
  { id: 'customize', label: 'Customize label' },
  { id: 'review', label: 'Review & print' },
];

const brandButton = {
  bg: KARAS_RED,
  color: 'white',
  _hover: { bg: '#732f2f' },
};

const flattenErrors = (
  errors: unknown,
  prefix = '',
): Record<string, string> => {
  const out: Record<string, string> = {};
  if (!errors || typeof errors !== 'object') return out;
  Object.entries(errors as Record<string, unknown>).forEach(([key, value]) => {
    const path = prefix ? key : key;
    if (Array.isArray(value)) out[path] = value.map(String).join(' ');
    else if (value && typeof value === 'object')
      Object.assign(out, flattenErrors(value, path));
    else if (value) out[path] = String(value);
  });
  return out;
};

const stableStringify = (value: unknown) => JSON.stringify(value);

interface LabelComposerProps {
  launch?: ComposerLaunch;
  variant: 'dialog' | 'page';
  onClose?: () => void;
  draftKey?: string;
}

export const LabelComposer: React.FC<LabelComposerProps> = ({
  launch,
  variant,
  onClose,
  draftKey,
}) => {
  const navigate = useNavigate();
  const userId = useAppSelector((s) => s.auth.current.userid);
  const storageKey = draftKey ?? `${DRAFT_PREFIX}${userId}`;
  const { data: formats, isLoading: formatsLoading } =
    TrackingApi.Api.useGetFormatsQuery();
  const { data: templates } = TrackingApi.Api.useGetTemplatesQuery();
  const { data: profiles } = TrackingApi.Api.useGetProfilesQuery();
  const { data: defaults, isSuccess: defaultsLoaded } =
    TrackingApi.Api.useGetDefaultsQuery();
  const [preview, { isLoading: previewLoading }] =
    TrackingApi.Api.usePostPreviewMutation();
  const [postJob, { isLoading: preparing }] =
    TrackingApi.Api.usePostJobMutation();
  const [postTemplate] = TrackingApi.Api.usePostTemplateMutation();
  const [patchTemplate] = TrackingApi.Api.usePatchTemplateMutation();
  const [postProfile] = TrackingApi.Api.usePostProfileMutation();
  const [patchProfile] = TrackingApi.Api.usePatchProfileMutation();
  const [putDefaults] = TrackingApi.Api.usePutDefaultsMutation();
  const [calibrate, { isLoading: calibrating }] =
    TrackingApi.Api.usePostCalibrationMutation();

  // The Labels page always starts on Choose items. A draft is restored only
  // when the print dialog explicitly continues into the full workspace.
  const continueDraft =
    !launch &&
    new URLSearchParams(window.location.search).get('continue') === '1';
  const restored = React.useMemo<Draft | null>(() => {
    if (!continueDraft) return null;
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as Draft) : null;
    } catch {
      return null;
    }
  }, [continueDraft, storageKey]);

  const [selections, setSelections] = React.useState<
    TrackingType.ComposerSelection[]
  >(restored?.selections ?? []);
  const [pendingSources, setPendingSources] = React.useState<
    TrackingType.ComposerSource[]
  >(restored?.pendingSources ?? launch?.sources ?? []);
  const [labelSpec, setLabelSpec] =
    React.useState<TrackingType.LabelSpec | null>(restored?.labelSpec ?? null);
  const [profileSpec, setProfileSpec] =
    React.useState<TrackingType.ProfileSpec | null>(
      restored?.profileSpec ?? null,
    );
  const [templateId, setTemplateId] = React.useState<number | null>(
    restored?.templateId ?? null,
  );
  const [profileId, setProfileId] = React.useState<number | null>(
    restored?.profileId ?? null,
  );
  const [unit, setUnit] = React.useState<'mm' | 'in'>(restored?.unit ?? 'mm');
  const [mode, setMode] = React.useState<'labels' | 'reports'>(
    restored?.mode ?? launch?.mode ?? 'labels',
  );
  const [paper, setPaper] = React.useState<'letter' | 'a4'>(
    restored?.paper ?? 'letter',
  );
  const opensOnCustomize = Boolean(
    launch?.codes?.length &&
    (launch.mode ?? 'labels') === 'labels' &&
    !(launch.sources ?? []).some((source) => source.source_type === 'frame'),
  );
  const [step, setStep] = React.useState<StepId>(
    restored?.step ?? (opensOnCustomize ? 'customize' : 'items'),
  );
  const [previewData, setPreviewData] =
    React.useState<TrackingType.PreviewResponse | null>(null);
  const [jobs, setJobs] = React.useState<{ id: string; hash: string }[]>([]);
  const [savingSettings, setSavingSettings] = React.useState(false);
  const [initialised, setInitialised] = React.useState(
    Boolean(restored?.labelSpec),
  );
  const previewSeq = React.useRef(0);
  const jobKey = React.useRef<{ key: string; hash: string } | null>(null);
  const prepareLock = React.useRef(false);
  const headingRef = React.useRef<HTMLHeadingElement>(null);
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const stepChosen = React.useRef(Boolean(restored?.step));
  const context = launch?.context ?? restored?.context;

  // Resolve launch codes into selections (live lookups; no writes).
  const { data: launchItems } = TrackingApi.Api.useGetItemsQuery(
    { codes: (launch?.codes ?? []).join(','), limit: 100 },
    { skip: !launch?.codes?.length },
  );
  React.useEffect(() => {
    if (!launch?.codes?.length || !launchItems) return;
    const byCode = new Map(launchItems.items.map((i) => [i.code, i]));
    setSelections(
      launch.codes
        .filter((c) => byCode.has(c))
        .map((c) => {
          const i = byCode.get(c)!;
          return {
            code: i.code,
            display_code: i.display_code,
            title: i.title,
            copies: 1,
            state: i.state,
          };
        }),
    );
  }, [launch?.codes, launchItems]);

  // Apply saved defaults once.
  React.useEffect(() => {
    if (initialised || !formats || !defaultsLoaded || !templates || !profiles)
      return;
    const template = templates.items.find(
      (t) => t.id === defaults?.template_id,
    );
    const profile = profiles.items.find(
      (p) => p.id === defaults?.printer_profile_id,
    );
    setLabelSpec(template?.spec ?? formats.defaults.label_spec);
    setTemplateId(template?.id ?? null);
    setProfileSpec(profile?.spec ?? formats.defaults.profile_spec);
    setProfileId(profile?.id ?? null);
    if (defaults?.preferences?.units) setUnit(defaults.preferences.units);
    if (defaults?.preferences?.paper) setPaper(defaults.preferences.paper);
    setInitialised(true);
  }, [initialised, formats, defaultsLoaded, defaults, templates, profiles]);

  // Persist the draft (session only; cleared on logout).
  React.useEffect(() => {
    if (!initialised) return;
    const draft: Draft = {
      selections,
      pendingSources,
      labelSpec,
      profileSpec,
      templateId,
      profileId,
      unit,
      mode,
      paper,
      step,
      context,
    };
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(draft));
    } catch {
      /* storage unavailable */
    }
  }, [
    initialised,
    selections,
    pendingSources,
    labelSpec,
    profileSpec,
    templateId,
    profileId,
    unit,
    mode,
    paper,
    step,
    context,
    storageKey,
  ]);

  React.useEffect(() => {
    if (!launch || !initialised || stepChosen.current) return;
    const frames = (launch.sources ?? []).some(
      (s) => s.source_type === 'frame',
    );
    if (launch.codes?.length) {
      if (!launchItems) return;
      const found = launch.codes.some((code) =>
        launchItems.items.some((item) => item.code === code),
      );
      if (found && selections.length === 0) return;
      if (selections.length > 0 && !frames) setStep('customize');
    }
    stepChosen.current = true;
  }, [launch, initialised, launchItems, selections.length]);

  const totalLabels = selections.reduce((n, s) => n + s.copies, 0);

  // Live, debounced preview with an out-of-order guard.
  React.useEffect(() => {
    if (!labelSpec || !profileSpec || mode !== 'labels') return;
    if (!selections.length && !pendingSources.length) {
      setPreviewData(null);
      return;
    }
    const seq = ++previewSeq.current;
    const handle = window.setTimeout(async () => {
      try {
        const res = await preview({
          label_spec: labelSpec,
          profile_spec: profileSpec,
          code: selections[0]?.code,
          source:
            !selections.length && pendingSources[0]
              ? pendingSources[0]
              : undefined,
          title: launch?.title,
          label_count: Math.max(totalLabels, 1),
        }).unwrap();
        if (seq === previewSeq.current) setPreviewData(res);
      } catch {
        if (seq === previewSeq.current) setPreviewData(null);
      }
    }, 300);
    return () => window.clearTimeout(handle);
  }, [
    labelSpec,
    profileSpec,
    selections,
    pendingSources,
    totalLabels,
    mode,
    preview,
    launch?.title,
  ]);

  const fieldErrors = React.useMemo(
    () =>
      flattenErrors(
        (previewData?.spec_errors as Record<string, unknown>) ?? {},
      ),
    [previewData],
  );
  const specMessages = Object.entries(fieldErrors).map(
    ([k, v]) => `${k.replace(/[._]/g, ' ')}: ${v}`,
  );

  const template = templates?.items.find((t) => t.id === templateId) ?? null;
  const profile = profiles?.items.find((p) => p.id === profileId) ?? null;
  const templateDirty = Boolean(
    template &&
    labelSpec &&
    stableStringify(template.spec) !== stableStringify(labelSpec),
  );
  const profileDirty = Boolean(
    profile &&
    profileSpec &&
    stableStringify(profile.spec) !== stableStringify(profileSpec),
  );
  const layoutOk =
    mode === 'reports' ||
    Boolean(
      previewData?.layout &&
      !previewData.layout.errors.length &&
      !previewData.media_issues?.length &&
      !previewData.spec_errors,
    );
  const canPrepare =
    selections.length > 0 &&
    layoutOk &&
    selections.every(
      (s) => !s.state || s.state === 'ACTIVE' || mode === 'reports',
    );

  const prepare = async () => {
    if (!labelSpec || !profileSpec || prepareLock.current) return;
    prepareLock.current = true;
    const body = {
      job_type: mode === 'reports' ? 'REPORTS' : 'LABELS',
      items: selections.map((s) => ({ code: s.code, copies: s.copies })),
      template_id: templateId && !templateDirty ? templateId : undefined,
      label_spec: labelSpec,
      profile_spec: profileSpec,
      paper,
      context: context ?? { page: 'tracking' },
    };
    const hash = stableStringify(body);
    if (!jobKey.current || jobKey.current.hash !== hash)
      jobKey.current = { key: newRequestId(), hash };
    try {
      const job = await postJob({
        ...body,
        idempotency_key: jobKey.current.key,
      }).unwrap();
      setJobs((prev) =>
        prev.some((j) => j.id === job.id)
          ? prev
          : [{ id: job.id, hash }, ...prev],
      );
      jobKey.current = null;
    } catch {
      /* the request key is kept so a retry of the same request cannot create a duplicate job */
    } finally {
      prepareLock.current = false;
    }
  };

  const saveSettings = async () => {
    if (!labelSpec || !profileSpec || savingSettings) return;
    setSavingSettings(true);
    try {
      const personalTemplate =
        template?.is_mine === true
          ? template
          : (templates?.items.find(
              (item) => item.is_mine && item.name === 'My label settings',
            ) ?? null);
      const savedTemplate = personalTemplate
        ? await patchTemplate({
            id: personalTemplate.id,
            revision_number: personalTemplate.revision_number,
            spec: labelSpec,
          }).unwrap()
        : await postTemplate({
            name: 'My label settings',
            is_shared: false,
            spec: labelSpec,
          }).unwrap();

      const personalProfile =
        profile?.is_mine === true
          ? profile
          : (profiles?.items.find(
              (item) => item.is_mine && item.name === 'My printer settings',
            ) ?? null);
      const savedProfile = personalProfile
        ? await patchProfile({
            id: personalProfile.id,
            revision: personalProfile.revision,
            spec: profileSpec,
          }).unwrap()
        : await postProfile({
            name: 'My printer settings',
            is_shared: false,
            spec: profileSpec,
          }).unwrap();

      await putDefaults({
        template_id: savedTemplate.id,
        printer_profile_id: savedProfile.id,
        preferences: { ...(defaults?.preferences ?? {}), units: unit, paper },
      }).unwrap();
      setTemplateId(savedTemplate.id);
      setLabelSpec(savedTemplate.spec);
      setProfileId(savedProfile.id);
      setProfileSpec(savedProfile.spec);
      toasterApi({
        type: 'success',
        status: 200,
        code: 'Success',
        message: 'Settings saved to your account',
      });
    } catch {
      /* reported globally */
    } finally {
      setSavingSettings(false);
    }
  };

  const onCalibration = async () => {
    if (!labelSpec || !profileSpec) return;
    try {
      const pdf = await calibrate({
        label_spec: labelSpec,
        profile_spec: profileSpec,
      }).unwrap();
      if (!printPdfBlob(pdf)) openPdfBlob(pdf);
    } catch {
      /* reported globally */
    }
  };

  const openWorkspace = () => {
    const draft: Draft = {
      selections,
      pendingSources,
      labelSpec,
      profileSpec,
      templateId,
      profileId,
      unit,
      mode,
      paper,
      step,
      context,
    };
    try {
      sessionStorage.setItem(`${DRAFT_PREFIX}${userId}`, JSON.stringify(draft));
    } catch {
      /* ignore */
    }
    onClose?.();
    navigate('/tracking?tab=labels&continue=1');
  };

  const requestBody = {
    job_type: mode === 'reports' ? 'REPORTS' : 'LABELS',
    items: selections.map((s) => ({ code: s.code, copies: s.copies })),
    template_id: templateId && !templateDirty ? templateId : undefined,
    label_spec: labelSpec,
    profile_spec: profileSpec,
    paper,
    context: context ?? { page: 'tracking' },
  };
  const requestHash = labelSpec && profileSpec ? stableStringify(requestBody) : '';
  const matchingJobs = jobs.filter((j) => j.hash === requestHash);

  const itemsBlocker = !selections.length
    ? pendingSources.some((s) => s.source_type === 'frame')
      ? 'Choose the physical units for this frame before continuing.'
      : pendingSources.length
        ? 'Register or choose a lot for the records above before continuing.'
        : 'Add at least one item to continue.'
    : selections.some((s) => s.state && s.state !== 'ACTIVE') && mode === 'labels'
      ? 'Remove items that are not active before printing labels.'
      : null;
  const settingsBlocker =
    mode === 'reports'
      ? null
      : !previewData
        ? previewLoading
          ? 'The preview is still updating.'
          : 'Wait for the label preview before review.'
        : !layoutOk
          ? specMessages[0] ||
            previewData.layout?.errors[0]?.message ||
            'Fix the label settings shown above before review.'
          : null;

  const go = (next: StepId) => {
    if (next !== 'items' && itemsBlocker) return;
    if (next === 'review' && settingsBlocker) return;
    setStep(next);
    window.setTimeout(() => {
      bodyRef.current?.scrollTo({ top: 0 });
      const heading = Array.from(
        bodyRef.current?.querySelectorAll('h2') ?? [],
      ).find((node) => node instanceof HTMLElement && node.offsetParent);
      if (heading instanceof HTMLElement) heading.focus();
    }, 0);
  };

  if (formatsLoading || !formats || !labelSpec || !profileSpec) {
    return (
      <HStack py={10} justify="center">
        <Spinner />
        <Text>Loading label settings…</Text>
      </HStack>
    );
  }

  const formatName =
    labelSpec.codes.mode === 'both'
      ? 'QR + Barcode'
      : (formats.formats.find((f) => f.id === labelSpec.codes.primary)?.label ??
        labelSpec.codes.primary);
  const stepIndex = STEPS.findIndex((s) => s.id === step);

  const itemsPanel = (
    <ItemsPanel
      selections={selections}
      maxCopies={formats.limits.copies_max ?? 100}
      onAdd={(items) =>
        setSelections((prev) => {
          const map = new Map(prev.map((s) => [s.code, s]));
          items.forEach((i) => {
            if (!map.has(i.code)) map.set(i.code, i);
          });
          return Array.from(map.values());
        })
      }
      onChangeCopies={(code, copies) =>
        setSelections((prev) =>
          prev.map((s) => (s.code === code ? { ...s, copies } : s)),
        )
      }
      onRemove={(code) =>
        setSelections((prev) => prev.filter((s) => s.code !== code))
      }
      pendingSources={pendingSources}
      onResolvePending={(source, items) => {
        setPendingSources((prev) =>
          prev.filter(
            (s) =>
              !(
                s.source_type === source.source_type &&
                s.source_id === source.source_id
              ),
          ),
        );
        if (items.length)
          setSelections((prev) => [
            ...prev,
            ...items.filter((i) => !prev.some((p) => p.code === i.code)),
          ]);
      }}
      onDropPending={(source) =>
        setPendingSources((prev) =>
          prev.filter(
            (s) =>
              !(
                s.source_type === source.source_type &&
                s.source_id === source.source_id
              ),
          ),
        )
      }
    />
  );

  return (
    <Stack
      gap={0}
      h={variant === 'dialog' ? '100%' : undefined}
      maxH={variant === 'page' ? 'calc(100dvh - 220px)' : undefined}
      maxW={variant === 'page' ? '1080px' : undefined}
      mx={variant === 'page' ? 'auto' : undefined}
      w="100%"
      minH={0}
    >
      <HStack
        justify="space-between"
        flexWrap="wrap"
        gap={2}
        px={variant === 'dialog' ? { base: 3, md: 5 } : 0}
        pt={variant === 'dialog' ? 3 : 0}
        pb={2}
      >
        <HStack gap={2}>
          <Button
            size="sm"
            variant={mode === 'labels' ? 'solid' : 'outline'}
            onClick={() => setMode('labels')}
          >
            <LuTag /> Labels
          </Button>
          <Button
            size="sm"
            variant={mode === 'reports' ? 'solid' : 'outline'}
            onClick={() => setMode('reports')}
          >
            <LuFileText /> Latest reports
          </Button>
        </HStack>
        {variant === 'dialog' && (
          <Button size="sm" variant="ghost" onClick={openWorkspace}>
            <LuExpand /> Open full workspace
          </Button>
        )}
      </HStack>

      {mode === 'labels' && (
        <HStack
          as="nav"
          aria-label="Label printing steps"
          gap={0}
          px={variant === 'dialog' ? { base: 3, md: 5 } : 0}
          pb={2}
          overflowX="auto"
        >
          {STEPS.map((s, index) => {
            const done = index < stepIndex;
            const current = s.id === step;
            const locked =
              (s.id !== 'items' && Boolean(itemsBlocker) && index > stepIndex) ||
              (s.id === 'review' && Boolean(settingsBlocker) && step !== 'review');
            return (
              <Button
                key={s.id}
                size="sm"
                variant="ghost"
                flex="1"
                minW={0}
                px={1}
                justifyContent="flex-start"
                aria-current={current ? 'step' : undefined}
                disabled={locked && !current}
                onClick={() => go(s.id)}
                borderBottomWidth="2px"
                borderColor={current ? KARAS_RED : 'transparent'}
                borderRadius={0}
              >
                <Text fontSize="xs" color="fg.muted" mr={1}>
                  {done && !current ? 'Done' : index + 1}
                </Text>
                <Text fontSize="xs" truncate>
                  {s.label}
                </Text>
              </Button>
            );
          })}
        </HStack>
      )}

      <Box
        ref={bodyRef}
        flex="1"
        minH={0}
        overflowY="auto"
        px={variant === 'dialog' ? { base: 3, md: 5 } : 0}
        py={3}
      >
        {mode === 'reports' ? (
          <Stack gap={4}>
            <Text as="h2" textStyle="heading.form" tabIndex={-1} ref={headingRef}>
              Latest reports
            </Text>
            {itemsPanel}
            <SettingsPanel
              formats={formats}
              labelSpec={labelSpec}
              profileSpec={profileSpec}
              unit={unit}
              onUnit={setUnit}
              onLabel={setLabelSpec}
              onProfile={setProfileSpec}
              fieldErrors={fieldErrors}
              mode={mode}
              paper={paper}
              onPaper={setPaper}
            />
          </Stack>
        ) : (
          <>
          <Box display={step === 'items' ? 'block' : 'none'}>{itemsPanel}</Box>
          <Box display={step === 'customize' ? 'block' : 'none'}>
          <Stack gap={4}>
            <Stack gap={1}>
              <Text
                as="h2"
                ref={headingRef}
                tabIndex={-1}
                textStyle="heading.form"
              >
                Customize label
              </Text>
              <Text fontSize="sm" color="fg.muted">
                Choose the code, the label size, and the text that prints. The
                preview updates as you change these. Save settings stores this
                setup on your account and uses it the next time you print.
                When it looks right, continue to Review &amp; print.
              </Text>
            </Stack>
            <HStack gap={3} flexWrap="wrap" align="flex-end">
              <Field.Root width={{ base: '100%', md: '260px' }}>
                <Field.Label fontSize="xs">Saved label template</Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    aria-label="Saved label template"
                    value={templateId ?? ''}
                    onChange={(e) => {
                      const t = templates?.items.find(
                        (x) => x.id === Number(e.target.value),
                      );
                      setTemplateId(t?.id ?? null);
                      if (t?.spec) setLabelSpec(t.spec);
                    }}
                  >
                    <option value="">Custom settings</option>
                    {templates?.items.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                        {t.is_shared ? ' (shared)' : ''}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
              <Field.Root width={{ base: '100%', md: '240px' }}>
                <Field.Label fontSize="xs">Saved printer profile</Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    aria-label="Saved printer profile"
                    value={profileId ?? ''}
                    onChange={(e) => {
                      const p = profiles?.items.find(
                        (x) => x.id === Number(e.target.value),
                      );
                      setProfileId(p?.id ?? null);
                      if (p?.spec) setProfileSpec(p.spec);
                    }}
                  >
                    <option value="">Custom printer settings</option>
                    {profiles?.items.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                        {p.is_shared ? ' (shared)' : ''}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
              {templateDirty && <Badge>Template modified</Badge>}
              {profileDirty && <Badge>Printer profile modified</Badge>}
            </HStack>
            <Grid
              templateColumns={{ base: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) minmax(280px, 0.8fr)' }}
              gap={5}
              alignItems="start"
            >
              <SettingsPanel
                formats={formats}
                labelSpec={labelSpec}
                profileSpec={profileSpec}
                unit={unit}
                onUnit={setUnit}
                onLabel={setLabelSpec}
                onProfile={setProfileSpec}
                fieldErrors={fieldErrors}
                mode={mode}
                paper={paper}
                onPaper={setPaper}
                onCalibration={onCalibration}
                calibrating={calibrating}
              />
              <Box position={{ lg: 'sticky' }} top={0}>
                <PreviewPanel
                  preview={previewData}
                  loading={previewLoading}
                  unit={unit}
                  labelCount={totalLabels}
                  mode={mode}
                  specErrorMessages={specMessages}
                />
              </Box>
            </Grid>
            <Collapsible.Root>
              <Collapsible.Trigger asChild>
                <Button variant="ghost" size="sm" px={0}>
                  <LuChevronDown /> Manage saved templates and printers
                </Button>
              </Collapsible.Trigger>
              <Collapsible.Content>
                <Box pt={2}>
                  <SavedSettingsManager />
                </Box>
              </Collapsible.Content>
            </Collapsible.Root>
          </Stack>
          </Box>
          <Box display={step === 'review' ? 'block' : 'none'}>
          <Stack gap={4}>
            <Text as="h2" ref={headingRef} tabIndex={-1} textStyle="heading.form">
              Review & print
            </Text>
            <PreviewPanel
              preview={previewData}
              loading={previewLoading}
              unit={unit}
              labelCount={totalLabels}
              mode={mode}
              specErrorMessages={specMessages}
            />
            <Stack gap={1} fontSize="sm">
              <HStack justify="space-between">
                <Text>
                  {selections.length} item{selections.length === 1 ? '' : 's'} ·{' '}
                  {totalLabels} label{totalLabels === 1 ? '' : 's'}
                </Text>
                <Button size="xs" variant="ghost" onClick={() => go('items')}>
                  Change items
                </Button>
              </HStack>
              <HStack justify="space-between">
                <Text>
                  {formatName} · {labelSpec.label.width_mm} × {labelSpec.label.height_mm} mm ·{' '}
                  {profileSpec.media === 'sheet' ? 'Sheet' : 'Roll'}
                </Text>
                <Button size="xs" variant="ghost" onClick={() => go('customize')}>
                  Change settings
                </Button>
              </HStack>
              <Text color="fg.muted">
                Template: {template && !templateDirty ? template.name : 'Custom settings'}
                {' · '}
                Printer: {profile && !profileDirty ? profile.name : 'Custom printer settings'}
              </Text>
              <Text color="fg.muted">
                Printing saves this as a job on each selected item. A label job
                records a printed label; a report job records a report.
              </Text>
              <Text color="fg.muted">Print at Actual size / 100%.</Text>
            </Stack>
            {settingsBlocker && (
              <Text fontSize="sm" color="red.fg" role="alert">
                {settingsBlocker}
              </Text>
            )}
            {matchingJobs.map((job) => (
              <JobPanel key={job.id} jobId={job.id} />
            ))}
            {jobs.some((j) => j.hash !== requestHash) && (
              <Text fontSize="xs" color="fg.muted">
                A previously prepared file no longer matches these settings.
                Print labels to prepare a new file.
              </Text>
            )}
          </Stack>
          </Box>
          </>
        )}
      </Box>

      <HStack
        justify="space-between"
        gap={2}
        flexWrap="wrap"
        borderTopWidth="1px"
        px={variant === 'dialog' ? { base: 3, md: 5 } : 0}
        py={3}
      >
        {mode === 'reports' ? (
          <Button
            {...brandButton}
            onClick={prepare}
            loading={preparing}
            disabled={!canPrepare}
          >
            Prepare {selections.length} latest report
            {selections.length === 1 ? '' : 's'}
          </Button>
        ) : (
          <>
            <HStack gap={2}>
              {step === 'customize' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={saveSettings}
                  loading={savingSettings}
                >
                  Save settings
                </Button>
              )}
              {step === 'items' && (
                <Button
                  {...brandButton}
                  onClick={() => go('customize')}
                  disabled={Boolean(itemsBlocker)}
                >
                  Customize label
                </Button>
              )}
              {step === 'customize' && (
                <Button
                  {...brandButton}
                  onClick={() => go('review')}
                  disabled={Boolean(settingsBlocker)}
                >
                  Review & print
                </Button>
              )}
              {step === 'review' && (
                <Button
                  {...brandButton}
                  onClick={prepare}
                  loading={preparing}
                  disabled={!canPrepare}
                >
                  Print {totalLabels} label{totalLabels === 1 ? '' : 's'}
                </Button>
              )}
            </HStack>
            <Button
              variant="ghost"
              disabled={step === 'items'}
              onClick={() => go(STEPS[Math.max(0, stepIndex - 1)].id)}
            >
              <LuChevronLeft /> Back
            </Button>
          </>
        )}
        {(itemsBlocker && step === 'items' && mode === 'labels') ||
        (settingsBlocker && step === 'customize' && mode === 'labels') ||
        (mode === 'reports' && !selections.length) ? (
          <Text fontSize="xs" color="fg.muted" role="status" w="100%">
            {mode === 'reports'
              ? 'Add at least one item to prepare reports.'
              : step === 'items'
                ? itemsBlocker
                : settingsBlocker}
          </Text>
        ) : null}
        {mode === 'reports' &&
          matchingJobs.map((job) => <JobPanel key={job.id} jobId={job.id} />)}
      </HStack>
    </Stack>
  );
};
